import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    listOoraPlans,
    addOoraPlan,
    updateOoraPlan,
    updateOoraPlanStatus
} from "../../Redux/Actions/ooraAction";
import { columnsOoraList } from "../../Global Functions/columnsAntd";
import TableComponent from "../../Global Functions/tableComponents";
import toast from "react-hot-toast";
import DateRangePopup from "../../Utils/dateFilterPopup";
import OoraPlanEditModal from "./addAndUpdateOoraPlan";
import { Button, Modal } from "antd";

const ListOoraPlans = () => {
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const fetchDetails = useCallback(
        (showLoader = true) => {
            const payload = {
                page: pagination.current,
                limit: pagination.pageSize,
                filter: debouncedSearch,
                is_deleted: false,
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };
            return dispatch(listOoraPlans(payload, showLoader)).catch(() => {
                if (showLoader) {
                    toast.error("Failed to fetch plans list");
                }
            });
        },
        [dispatch, pagination.current, pagination.pageSize, debouncedSearch, dateRange]
    );


    const ooraPlanState = useSelector((state) => state.oora || {});
    const plans = useMemo(() => {
        return ooraPlanState.plans?.data || [];
    }, [ooraPlanState.plans]);
    const planTotal = useMemo(() => {
        return ooraPlanState.plans?.total || 0;
    }, [ooraPlanState.plans]);
    const { loading } = ooraPlanState;

    useEffect(() => {
        fetchDetails(true);
    }, [fetchDetails]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, total: planTotal }));
    }, [planTotal]);

    const handleDateRangeApply = (start, end) => {
        if (start === null && end === null) {
            setDateRange({ start: null, end: null });
        } else {
            setDateRange({ start, end });
        }
        setIsDatePopupVisible(false);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);

        return () => clearTimeout(handler);
    }, [searchQuery]);


    const openEditModal = useCallback((plan = null) => {
        setSelectedPlan(plan);
        setIsModalOpen(true);
    }, []);

    const closeEditModal = () => {
        setSelectedPlan(null);
        setIsModalOpen(false);
    };

    const handleDelete = useCallback((planId) => {
        const plan = plans.find(p => p._id === planId);
        const isActive = plan?.status === "active";

        Modal.confirm({
            title: `Confirm ${isActive ? "Deactivate" : "Activate"}`,
            content: `Are you sure you want to ${isActive ? "deactivate" : "activate"} this plan?`,
            okText: `Yes, ${isActive ? "Deactivate" : "Activate"}`,
            okType: isActive ? "danger" : "primary",
            onOk: async () => {
                try {
                    await dispatch(updateOoraPlanStatus({
                        id: planId,
                        status: isActive ? "inactive" : "active",
                        login_id: "admin_user_id"
                    }));
                    toast.success(`Plan ${isActive ? "deactivated" : "activated"} successfully`);
                    fetchDetails(false);
                } catch (error) {
                    toast.error("Failed to update plan status");
                }
            },
        });
    }, [dispatch, plans, fetchDetails]);

    const handleSubmit = async (values) => {
        const prevPlans = ooraPlanState.plans?.data || [];

        const isDuplicatePlan = prevPlans.some(
            (plan) =>
                plan.plan_name_withoutspace === values.plan_name?.replace(/\s+/g, '').toLowerCase() &&
                plan._id !== selectedPlan?._id
        );

        if (isDuplicatePlan) {
            toast.error("Plan name already exists");
            return { success: false };
        }

        try {
            let result;
            if (selectedPlan?._id) {
                result = await dispatch(updateOoraPlan({
                    id: selectedPlan._id,
                    ...values
                }));
            } else {
                result = await dispatch(addOoraPlan({
                    ...values
                }));
            }

            if (result) {
                fetchDetails(false);
                closeEditModal();
                return { success: true };
            }
        } catch (error) {
            toast.error("Failed to save plan");
            return { success: false };
        }
    };

    const handleTableChange = (pagination) => {
        setPagination(pagination);
        fetchDetails(false);
    };

    return (
        <>
            <div style={{ position: "absolute", marginTop: "9.6%", marginLeft: "86.5%" }}>
                <Button
                    style={{ background: "#0D1A52", color: "white", padding: "18px", borderRadius: "20px" }}
                    onClick={() => openEditModal(null)}
                >
                    Add Plan
                </Button>
            </div>

            <TableComponent
                pagination={pagination}
                setState={setSearchQuery}
                dataSource={plans}
                onChange={handleTableChange}
                h3heading="Oora Plans List"
                columns={columnsOoraList(pagination.current, pagination.pageSize, handleDelete, openEditModal)}
                loading={loading}
            />

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />

            {isModalOpen && (
                <OoraPlanEditModal
                    visible={isModalOpen}
                    planData={selectedPlan}
                    onSave={handleSubmit}
                    onClose={closeEditModal}
                />
            )}
        </>
    );
};

export default ListOoraPlans;
